import { useEffect, useState } from 'react'
import { type LoaderFunction, json } from '@remix-run/node'
import { Outlet, useLoaderData, useNavigate, useMatches, useOutletContext, useLocation } from '@remix-run/react'

import { NavigationLoading, Step, Stepper } from '~/components/shared'
import { type AffiliateRouteSlug, type AffiliateOutletData, ROUTES } from '~/components/affiliate/Affiliate.config'
import ApiClient from '~/services/api-client.server'
import { handleError } from '~/services/error-handler.server'
import { getCountriesList } from '~/services/countries.server'
import type { Language } from '~/types/api-client'
import type KeyValuePair from '~/types/key-value-pair'

type LoaderData = {
  countryCode: string | null
  countries: KeyValuePair[]
  communicationLanguages: Language[]
}

export const loader: LoaderFunction = async ({ request }) => {
  let headers = new Headers()

  const apiClient = new ApiClient(null, request.headers)

  const countries = await getCountriesList(request, headers)

  let communicationLanguages: Language[] = []

  try {
    communicationLanguages = await apiClient.getCommunicationLanguages()
  } catch (error: any) {
    const handledError = await handleError(error, request)

    throw new Response(handledError.message, {
      status: handledError.response?.status ?? 500,
      headers: headers,
    })
  }

  const countryCode = request.headers.get('cf-ipcountry')

  return json<LoaderData>(
    {
      countryCode,
      countries,
      communicationLanguages,
    },
    { headers: headers },
  )
}

export default function AffiliateLayout() {
  const { countryCode, countries, communicationLanguages } = useLoaderData<LoaderData>()
  const navigate = useNavigate()
  const matches = useMatches()
  const parentOutletData = useOutletContext()

  const [personalInfo, setPersonalInfo] = useState<Record<string, any>>({})
  const [businessDetails, setBusinessDetails] = useState<Record<string, any>>({})
  const [audienceInsights, setAudienceInsights] = useState<Record<string, any>>({})
  const [partnershipGoals, setPartnershipGoals] = useState<Record<string, any>>({})
  const [additionalInfo, setAdditionalInfo] = useState<Record<string, any>>({})

  const currentRouteId = matches[matches.length - 1]?.id

  const steps: {
    label: string
    slug: AffiliateRouteSlug
    route: string
    disabled: boolean
  }[] = [
    {
      label: 'Personal Info',
      slug: 'personal-info',
      route: ROUTES.personalInfo,
      disabled: false,
    },
    {
      label: 'Business Details',
      slug: 'business-details',
      route: ROUTES.businessDetails,
      disabled: Object.keys(personalInfo).length === 0,
    },
    {
      label: 'Audience Insights',
      slug: 'audience-insights',
      route: ROUTES.audienceInsights,
      disabled: Object.keys(businessDetails).length === 0,
    },
    {
      label: 'Partnership Goals',
      slug: 'partnership-goals',
      route: ROUTES.partnershipGoals,
      disabled: Object.keys(audienceInsights).length === 0,
    },
    {
      label: 'Additional Info',
      slug: 'additional-info',
      route: ROUTES.additionalInfo,
      disabled: Object.keys(partnershipGoals).length === 0,
    },
  ]

  const activeStep = steps.findIndex(({ slug }) => currentRouteId === `routes/affiliate/${slug}`)

  const outletContext: AffiliateOutletData = {
    ...(parentOutletData as { mainDomainUrl: string }),

    countryCode,
    countries,
    communicationLanguages,

    personalInfo,
    setPersonalInfo,
    businessDetails,
    setBusinessDetails,
    audienceInsights,
    setAudienceInsights,
    partnershipGoals,
    setPartnershipGoals,
    additionalInfo,
    setAdditionalInfo,
  }
  const { pathname, search } = useLocation()

  useEffect(
    function redirectIfNotCompletedFirstStep() {
      if (pathname.replace(/\/+$/, '') !== ROUTES.personalInfo && Object.keys(personalInfo).length === 0) {
        navigate(ROUTES.personalInfo + search)
      }
    },
    [navigate, pathname, personalInfo, search],
  )

  return (
    <NavigationLoading>
      <div className="mx-auto my-8 flex max-w-screen-xl flex-col gap-3 px-4 sm:gap-12">
        <Stepper activeStep={activeStep} className="scrollbar-none overflow-x-auto">
          {steps.map((step) => {
            return (
              <Step
                key={step.label}
                className={step.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                onClick={() => !step.disabled && navigate(step.route)}
              >
                {step.label}
              </Step>
            )
          })}
        </Stepper>
        <Outlet context={outletContext} />
      </div>
    </NavigationLoading>
  )
}
